/**
 * NotificationPanel component
 * Dropdown panel displaying notification list with mark-all-read functionality
 */

import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { CheckCheck, BellOff } from 'lucide-react'
import { useRealtimeNotifications } from '../../dashboard/hooks/use-realtime-notifications'
import { useMarkAllRead } from '../hooks/use-mark-all-read'
import { NotificationItem } from './notification-item'
import { supabase } from '../../../lib/supabase'
import type { Notification } from '../../dashboard/types/dashboard.types'

interface NotificationPanelProps {
  open: boolean
  onClose: () => void
}

export function NotificationPanel({ open, onClose }: NotificationPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { notifications, markAsRead, loading } = useRealtimeNotifications()
  const { markAllRead, loading: markAllLoading } = useMarkAllRead()

  // Close on outside click
  useEffect(() => {
    if (!open) return

    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open, onClose])

  // Close on Escape key
  useEffect(() => {
    if (!open) return

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [open, onClose])

  const handleMarkAllRead = async () => {
    await markAllRead()
    // Notifications will be updated via realtime subscription
  }

  const handleNavigate = async (notification: Notification) => {
    if (!notification.is_read) markAsRead(notification.id)

    if (notification.entity_type === 'engagement_stage' && notification.entity_id) {
      const { data } = await supabase
        .from('engagement_stages')
        .select('engagement_id')
        .eq('id', notification.entity_id)
        .maybeSingle()
      if (data?.engagement_id) {
        navigate(`/engagements?id=${data.engagement_id}&tab=milestones`)
      }
    }

    onClose()
  }

  if (!open) return null

  return (
    <div className="notification-panel" ref={panelRef}>
      <div className="notification-panel-header">
        <h3 className="notification-panel-title">Notifications</h3>
        {notifications.length > 0 && (
          <button
            className="notification-panel-action"
            onClick={handleMarkAllRead}
            disabled={markAllLoading}
            title="Mark all as read"
          >
            <CheckCheck size={16} />
            <span>Mark all read</span>
          </button>
        )}
      </div>

      <div className="notification-panel-body">
        {loading && notifications.length === 0 ? (
          <div className="notification-empty">
            <p>Loading notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="notification-empty">
            <BellOff size={48} />
            <p>No notifications</p>
          </div>
        ) : (
          <div className="notification-list">
            {notifications.map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onMarkRead={markAsRead}
                onNavigate={handleNavigate}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
